import type React from 'react';
import { ComboBox, Input, ListBox } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ParentAccountOption } from './accountFormMeta';

type AccountParentPickerProps = {
    ariaLabel: string;
    value: string | null;
    options: ParentAccountOption[];
    placeholder: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
    onChange: (accountId: string | null) => void;
};

/**
 * Parent-account picker — the group-account inverse of the Journal's
 * `AccountPicker`. Same HeroUI React-Aria `ComboBox`, but the options passed in
 * are the **non-postable (group)** accounts (built in `useAccountForm`), since a
 * parent is a header, not a posting leaf. Each option's `textValue` is
 * `"code name"`, so the built-in contains-filter matches the code or any part of
 * the localized name, in either locale; fully keyboard-operable.
 *
 * `getNodeTestIdProp` (data-testid only, no `id`) is used deliberately — an
 * explicit `id` on the RAC ComboBox root triggers a setState loop.
 */
export const AccountParentPicker: React.FC<AccountParentPickerProps> = ({
    ariaLabel,
    value,
    options,
    placeholder,
    isDisabled,
    isInvalid,
    onChange,
}) => (
    <ComboBox<ParentAccountOption>
        aria-label={ariaLabel}
        defaultItems={options}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        menuTrigger="focus"
        onSelectionChange={(key) => onChange(key === null ? null : String(key))}
        selectedKey={value}
        {...getNodeTestIdProp('account-form-parent')}
    >
        <ComboBox.InputGroup>
            <Input placeholder={placeholder} />
            <ComboBox.Trigger />
        </ComboBox.InputGroup>
        <ComboBox.Popover>
            <ListBox>
                {(item: ParentAccountOption) => (
                    <ListBox.Item id={item.id} textValue={item.searchText}>
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="shrink-0 text-xs tabular-nums text-muted">
                                {item.code}
                            </span>
                            <span className="truncate">{item.name}</span>
                        </span>
                    </ListBox.Item>
                )}
            </ListBox>
        </ComboBox.Popover>
    </ComboBox>
);
